import type {
  BinderAppearance,
  BinderLayout,
  BinderModel,
  BinderPlacement,
  BinderPlacementDraft,
  CollectionEntry,
  RiftboundCard,
  UserBinder,
} from '../types';
import { SETS, setReleaseDate } from '../constants';
import { isOvernumberedCard } from '../../shared/overnumbered.js';

function normalizeSetCode(setCode: string | undefined): string {
  return (setCode || '').trim().toUpperCase();
}

const SET_METADATA_ORDER = new Map(
  SETS.map((set, index) => [set.code.toUpperCase(), index] as const),
);

function compareSetCodesByReleaseDate(a: string, b: string): number {
  const normalizedA = normalizeSetCode(a);
  const normalizedB = normalizeSetCode(b);
  const dateA = setReleaseDate(normalizedA);
  const dateB = setReleaseDate(normalizedB);

  if (dateA && dateB && dateA !== dateB) return dateA.localeCompare(dateB);
  if (dateA && !dateB) return -1;
  if (!dateA && dateB) return 1;

  const indexA = SET_METADATA_ORDER.get(normalizedA) ?? Number.MAX_SAFE_INTEGER;
  const indexB = SET_METADATA_ORDER.get(normalizedB) ?? Number.MAX_SAFE_INTEGER;
  if (indexA !== indexB) return indexA - indexB;

  return normalizedA.localeCompare(normalizedB);
}

/** Set binders auto-created on first shelf visit: dated sets excluding OGS. Includes PG2 (collection) and upcoming sets. */
export const DEFAULT_SET_BINDER_CODES = SETS
  .filter((set) => set.releaseDate && set.code.toUpperCase() !== 'OGS')
  .map((set) => set.code.toUpperCase())
  .sort(compareSetCodesByReleaseDate);

const DEFAULT_SET_BINDER_CODE_SET = new Set(DEFAULT_SET_BINDER_CODES);

/** True for auto-seeded main-set albums (OGN, SFD, UNL, …). OGS and manual-only sets are not default. */
export function isDefaultSetBinder(
  binder: Pick<UserBinder, 'kind' | 'setCode'>,
): boolean {
  if (binder.kind !== 'set') return false;
  const code = normalizeSetCode(binder.setCode);
  return code.length > 0 && DEFAULT_SET_BINDER_CODE_SET.has(code);
}

export function isBinderDeletable(
  binder: Pick<UserBinder, 'kind' | 'setCode'>,
): boolean {
  return !isDefaultSetBinder(binder);
}

export const DEFAULT_BINDER_LAYOUT: BinderLayout = '3x3';

/** Smaller sets default to 3×3 (Zipfolio 360). Oversized sets bump via setBinderLayoutForCatalog. */
export const DEFAULT_SET_BINDER_LAYOUT: BinderLayout = '3x3';

/** Tiny promo/box sets (≤6 cards) pack onto a single 3×2 page. */
export const COMPACT_SET_BINDER_LAYOUT: BinderLayout = '3x2';

export const MEDIUM_SET_BINDER_LAYOUT: BinderLayout = '4x3';

export const LARGE_SET_BINDER_LAYOUT: BinderLayout = '4x4';

/**
 * The first open spread hides the left page (cover-adjacent). Start card slots on
 * page 1 so low collector numbers — including runes like OGN 007/007a — are visible.
 */
export const SET_BINDER_FIRST_PAGE_INDEX = 1;

export const DEFAULT_BINDER_APPEARANCE: BinderAppearance = {
  coverMode: 'default',
  color: '#f7f1df',
  textColor: '#31251d',
  material: 'fabric',
};

export const BINDER_LAYOUTS: Record<
  BinderLayout,
  {
    rows: number;
    columns: number;
    label: string;
    /**
     * When `true` the pocket grid holds cards rotated 90° (landscape).
     * The page width formula uses a 7:5 card aspect ratio instead of 5:7,
     * and each pocket's height is pocketW × (5/7) instead of pocketW × (7/5).
     * Applies to the Zipfolio 320 QuadRow 8-Pocket layout.
     */
    landscape?: boolean;
  }
> = {
  '2x2': { rows: 2, columns: 2, label: '2×2 Pocket (4/side)' },
  // Product name is columns×rows: 3×2 = 3 columns, 2 rows (6-pocket page for tiny sets).
  '3x2': { rows: 2, columns: 3, label: '3×2 Pocket (6/side)' },
  // Cards rotated 90° (landscape). In the physical Zipfolio 320 the binder itself is
  // landscape; in our portrait scene we represent it as 4 rows × 2 landscape pockets.
  // Designed for playset display. Riftbound allows 3 copies max, so players typically
  // place 3 copies across 3 adjacent landscape pockets within a column.
  '4x2': { rows: 4, columns: 2, label: '4×2 Pocket (8/side, landscape)', landscape: true },
  '3x3': { rows: 3, columns: 3, label: '3×3 Pocket (9/side)' },
  // Product name is columns×rows: 4×3 = 4 columns, 3 rows (Zipfolio 480 / 12-pocket).
  '4x3': { rows: 3, columns: 4, label: '4×3 Pocket (12/side)' },
  '4x4': { rows: 4, columns: 4, label: '4×4 Pocket (16/side)' },
};

// ── Binder Models ─────────────────────────────────────────────────────────

/**
 * Known physical binder models with fixed page counts.
 * `physicalPages` is the manufacturer's page count (each physical page = 2 single-sided
 * pages in our internal system, i.e. front + back of one leaf).
 */
export const BINDER_MODELS: BinderModel[] = [
  {
    id: 'zipfolio-160',
    label: 'Zipfolio 160 (4-Pocket)',
    brand: 'Zipfolio',
    layout: '2x2',
    physicalPages: 20,
    slotsPerSide: 4,
    totalSlots: 160,
  },
  {
    id: 'zipfolio-320',
    label: 'Zipfolio 320 (8-Pocket / QuadRow)',
    brand: 'Zipfolio',
    layout: '4x2',
    physicalPages: 20,
    slotsPerSide: 8,
    totalSlots: 320,
  },
  {
    id: 'zipfolio-360',
    label: 'Zipfolio 360 (9-Pocket)',
    brand: 'Zipfolio',
    layout: '3x3',
    physicalPages: 20,
    slotsPerSide: 9,
    totalSlots: 360,
  },
  {
    id: 'zipfolio-480',
    label: 'Zipfolio 480 (12-Pocket)',
    brand: 'Zipfolio',
    layout: '4x3',
    physicalPages: 20,
    slotsPerSide: 12,
    totalSlots: 480,
  },
  {
    id: 'zipfolio-640',
    label: 'Zipfolio 640 (16-Pocket)',
    brand: 'Zipfolio',
    layout: '4x4',
    physicalPages: 20,
    slotsPerSide: 16,
    totalSlots: 640,
  },
];

export const BINDER_MODELS_BY_ID: Record<string, BinderModel> = Object.fromEntries(
  BINDER_MODELS.map((m) => [m.id, m]),
);

/**
 * Converts a model's physical page count to the internal single-sided page limit
 * used by `UserBinder.pageLimit` (each physical leaf contributes 2 pages).
 */
export function binderModelPageLimit(model: BinderModel): number {
  return model.physicalPages * 2;
}

/** Default set album: Zipfolio 480 (20 physical leaves → 40 single-sided pages). */
export const SET_BINDER_DEFAULT_PAGE_LIMIT = binderModelPageLimit(BINDER_MODELS_BY_ID['zipfolio-480']);

/** Default custom binder without a physical model: 20 leaves → 40 single-sided pages. */
export const CUSTOM_BINDER_DEFAULT_PAGE_LIMIT = binderModelPageLimit(BINDER_MODELS_BY_ID['zipfolio-360']);

/** Pages added per custom-binder expansion (2 physical leaves). */
export const CUSTOM_BINDER_PAGE_INCREMENT = 4;

export function binderPhysicalPageCount(pageLimit: number): number {
  return pageLimit / 2;
}

export function nextCustomBinderPageLimit(currentPageLimit: number): number {
  return currentPageLimit + CUSTOM_BINDER_PAGE_INCREMENT;
}

/**
 * The last open spread hides its right page (back cover). When set content ends
 * on an odd page index it sits on that hidden side — pad with a trailing spread.
 */
export function setBinderTrailingPagePad(maxContentPageIndex: number): number {
  return maxContentPageIndex % 2 === 1 ? 2 : 0;
}

export function binderSlotsPerPage(layout: BinderLayout): number {
  const spec = BINDER_LAYOUTS[layout] ?? BINDER_LAYOUTS[DEFAULT_BINDER_LAYOUT];
  return spec.rows * spec.columns;
}

/** Layout used for rendering pockets. Set binders pick 3×3, 4×3, or 4×4 by set size. */
export function resolveBinderLayout(
  binder: Pick<UserBinder, 'kind' | 'layout' | 'modelId' | 'setCode'> | null | undefined,
): BinderLayout {
  if (!binder) return DEFAULT_BINDER_LAYOUT;
  if (binder.modelId) {
    const model = BINDER_MODELS_BY_ID[binder.modelId];
    if (model?.layout) return model.layout;
  }
  if (binder.kind === 'set') {
    if (binder.setCode) return setBinderLayoutForSetCode(binder.setCode);
    return DEFAULT_SET_BINDER_LAYOUT;
  }
  return binder.layout || DEFAULT_BINDER_LAYOUT;
}

/**
 * Pages required for a set catalog in the given layout, including trailing spread pad.
 */
function setBinderPagesNeeded(cards: RiftboundCard[], layout: BinderLayout): number {
  const positions = buildSetBinderSlotPositions(cards, layout);
  if (positions.size === 0) return 0;
  const maxContentPageIndex = Math.max(...[...positions.values()].map((position) => position.pageIndex));
  return maxContentPageIndex + 1 + setBinderTrailingPagePad(maxContentPageIndex);
}

/**
 * Picks the smallest pocket layout that fits a 20-leaf Zipfolio (40 internal pages).
 * Use when the complete set catalog is available (e.g. binder creation).
 */
export function setBinderLayoutForCatalog(cards: RiftboundCard[]): BinderLayout {
  if (!cards.length) return DEFAULT_SET_BINDER_LAYOUT;
  // Tiny sets (e.g. Arcane's 6 champions) use a single 3×2 page.
  if (cards.length <= binderSlotsPerPage(COMPACT_SET_BINDER_LAYOUT)) {
    return COMPACT_SET_BINDER_LAYOUT;
  }
  const zipfolioPageLimit = binderModelPageLimit(BINDER_MODELS_BY_ID['zipfolio-480']);

  for (const layout of [DEFAULT_SET_BINDER_LAYOUT, MEDIUM_SET_BINDER_LAYOUT, LARGE_SET_BINDER_LAYOUT] as const) {
    if (setBinderPagesNeeded(cards, layout) <= zipfolioPageLimit) return layout;
  }
  return LARGE_SET_BINDER_LAYOUT;
}

/** Fast layout lookup when only the set code is known (fallback until catalog loads). */
export function setBinderLayoutForSetCode(setCode: string): BinderLayout {
  const normalized = (setCode || '').trim().toUpperCase();
  if (normalized === 'ARC') return COMPACT_SET_BINDER_LAYOUT;
  if (normalized === 'OGN') return MEDIUM_SET_BINDER_LAYOUT;
  return DEFAULT_SET_BINDER_LAYOUT;
}

/** True when the full catalog fits a 20-leaf Zipfolio in the given layout. */
export function setBinderCatalogFitsZipfolio(cards: RiftboundCard[], layout: BinderLayout): boolean {
  return setBinderPagesNeeded(cards, layout) <= SET_BINDER_DEFAULT_PAGE_LIMIT;
}

export function binderSlotsPerSpread(layout: BinderLayout): number {
  return binderSlotsPerPage(layout) * 2;
}

export function binderSlotKey(pageIndex: number, slotIndex: number): string {
  return `p${pageIndex}-s${slotIndex}`;
}

export function binderSlotPositionForIndex(
  absoluteIndex: number,
  layout: BinderLayout,
): { pageIndex: number; slotIndex: number; slotKey: string } {
  const slotsPerPage = binderSlotsPerPage(layout);
  const pageIndex = Math.floor(absoluteIndex / slotsPerPage);
  const slotIndex = absoluteIndex % slotsPerPage;
  return {
    pageIndex,
    slotIndex,
    slotKey: binderSlotKey(pageIndex, slotIndex),
  };
}

export function binderPageSlots(
  pageIndex: number,
  layout: BinderLayout,
): Array<{ pageIndex: number; slotIndex: number; slotKey: string }> {
  return Array.from({ length: binderSlotsPerPage(layout) }, (_, slotIndex) => ({
    pageIndex,
    slotIndex,
    slotKey: binderSlotKey(pageIndex, slotIndex),
  }));
}

function collectorNumberParts(card: Pick<RiftboundCard, 'number' | 'riftboundId'>): {
  number: number;
  suffix: string;
} {
  const raw = card.number || card.riftboundId?.split('-')[1] || '';
  const match = raw.match(/(\d+)\s*([a-z]*)/i);
  if (!match) return { number: Number.MAX_SAFE_INTEGER, suffix: raw };
  return {
    number: Number(match[1]),
    suffix: match[2]?.toLowerCase() ?? '',
  };
}

export function sortBinderCardsByCollectorNumber(cards: RiftboundCard[]): RiftboundCard[] {
  return [...cards].sort((a, b) => {
    const setCompare = (a.setCode || '').localeCompare(b.setCode || '');
    if (setCompare !== 0) return setCompare;
    const aNum = collectorNumberParts(a);
    const bNum = collectorNumberParts(b);
    if (aNum.number !== bNum.number) return aNum.number - bNum.number;
    const suffixCompare = aNum.suffix.localeCompare(bNum.suffix);
    if (suffixCompare !== 0) return suffixCompare;
    return a.name.localeCompare(b.name);
  });
}

function nextPageAlignedIndex(absoluteIndex: number, layout: BinderLayout): number {
  const slotsPerPage = binderSlotsPerPage(layout);
  return Math.ceil(absoluteIndex / slotsPerPage) * slotsPerPage;
}

/** Columns per row for the layout (used for row-gap section breaks). */
function binderColumnsPerRow(layout: BinderLayout): number {
  return BINDER_LAYOUTS[layout]?.columns ?? BINDER_LAYOUTS[DEFAULT_BINDER_LAYOUT].columns;
}

/** Tokens sort after legends but share their spacing group — no break rows or page gap. */
function setBinderSpacingGroup(pageGroup: string): string {
  if (pageGroup === 'token') return 'legend-signature';
  return pageGroup;
}

/** Empty rows left between collector sections when continuing on the same page. */
const SET_BINDER_BREAK_ROWS = 2;

function setBinderGroupSizes(cards: RiftboundCard[]): Map<string, number> {
  const sizes = new Map<string, number>();
  for (const card of setBinderSectionOrderedCards(cards)) {
    const group = setBinderPageGroup(card);
    sizes.set(group, (sizes.get(group) ?? 0) + 1);
  }
  return sizes;
}

function inlineSetBinderGroupStartIndex(absoluteIndex: number, layout: BinderLayout): number {
  const columns = binderColumnsPerRow(layout);
  const slotInPage = absoluteIndex % binderSlotsPerPage(layout);
  const lastOccupiedSlot = slotInPage - 1;
  const lastRow = Math.floor(lastOccupiedSlot / columns);
  const startRow = lastRow + SET_BINDER_BREAK_ROWS + 1;
  const pageBase = Math.floor(absoluteIndex / binderSlotsPerPage(layout)) * binderSlotsPerPage(layout);
  return pageBase + startRow * columns;
}

/**
 * When a collector section ends mid-page, start the next section on the same page
 * (after break rows) only when the full incoming section fits in the space left.
 */
function nextSetBinderGroupStartIndex(
  absoluteIndex: number,
  layout: BinderLayout,
  previousGroup: string,
  nextGroup: string,
  nextGroupSize: number,
): number {
  const slotsPerPage = binderSlotsPerPage(layout);
  const columns = binderColumnsPerRow(layout);
  const slotInPage = absoluteIndex % slotsPerPage;

  if (slotInPage === 0) return absoluteIndex;

  const remainingOnPage = slotsPerPage - slotInPage;
  const minSlotsForInlineBreak = columns * (SET_BINDER_BREAK_ROWS + 1);

  if (remainingOnPage < minSlotsForInlineBreak) {
    return nextPageAlignedIndex(absoluteIndex, layout);
  }

  const inlineStart = inlineSetBinderGroupStartIndex(absoluteIndex, layout);
  const slotsAvailable = slotsPerPage - (inlineStart % slotsPerPage);

  if (nextGroupSize > slotsAvailable) {
    return nextPageAlignedIndex(absoluteIndex, layout);
  }

  return inlineStart;
}

function cardPrintSuffixMatches(card: Pick<RiftboundCard, 'name'>, suffix: string): boolean {
  return new RegExp(`\\(\\s*${suffix}\\s*\\)\\s*$`, 'i').test(card.name ?? '');
}

function isLegendOrSignatureCard(card: Pick<RiftboundCard, 'cardType' | 'supertype' | 'signature' | 'name'>): boolean {
  const cardType = (card.cardType ?? '').trim().toLowerCase();
  const supertype = (card.supertype ?? '').trim().toLowerCase();
  return (
    cardType === 'legend' ||
    supertype === 'signature' ||
    card.signature === true ||
    cardPrintSuffixMatches(card, 'Signed') ||
    cardPrintSuffixMatches(card, 'Signature')
  );
}

function isTokenCard(card: Pick<RiftboundCard, 'supertype' | 'riftboundId'>): boolean {
  const supertype = (card.supertype ?? '').trim().toLowerCase();
  if (supertype === 'token') return true;

  const collectorPart = card.riftboundId?.split('-')[1]?.trim().toLowerCase();
  return Boolean(collectorPart?.startsWith('t'));
}

function setBinderPageGroup(card: RiftboundCard): string {
  if (isOvernumberedCard(card)) return 'overnumbered';
  if ((card.cardType ?? '').trim().toLowerCase() === 'battlefield') return 'battlefield';
  if (isTokenCard(card)) return 'token';
  if (isLegendOrSignatureCard(card)) return 'legend-signature';

  const domains = (card.domain ?? [])
    .map((domain) => domain.trim().toLowerCase())
    .filter((domain) => domain !== 'colorless')
    .filter(Boolean);
  return domains.length > 0 ? `domain:${domains.join('|')}` : 'domain:none';
}

function setBinderSectionOrderedCards(cards: RiftboundCard[]): RiftboundCard[] {
  const sortedCards = sortBinderCardsByCollectorNumber(cards);
  const domainGroupOrder = new Map<string, number>();

  for (const card of sortedCards) {
    const group = setBinderPageGroup(card);
    if (group.startsWith('domain:') && !domainGroupOrder.has(group)) {
      domainGroupOrder.set(group, domainGroupOrder.size);
    }
  }

  const groupRank = (group: string): number => {
    if (group.startsWith('domain:')) return domainGroupOrder.get(group) ?? 99;
    if (group === 'domain:none') return 100;
    if (group === 'battlefield') return 200;
    if (group === 'legend-signature') return 300;
    if (group === 'token') return 400;
    if (group === 'overnumbered') return 500;
    return 600;
  };

  return sortedCards.sort((a, b) => {
    const groupCompare = groupRank(setBinderPageGroup(a)) - groupRank(setBinderPageGroup(b));
    if (groupCompare !== 0) return groupCompare;
    const aNum = collectorNumberParts(a);
    const bNum = collectorNumberParts(b);
    if (aNum.number !== bNum.number) return aNum.number - bNum.number;
    const suffixCompare = aNum.suffix.localeCompare(bNum.suffix);
    if (suffixCompare !== 0) return suffixCompare;
    return a.name.localeCompare(b.name);
  });
}

/**
 * Set binders follow collector sections. Domains and special types may continue on
 * the same page when break rows fit and the entire next section fits below.
 * Tokens flow with legends (no separate spacing group).
 */
export function buildSetBinderSlotPositions(
  cards: RiftboundCard[],
  layout: BinderLayout,
): Map<string, { pageIndex: number; slotIndex: number; slotKey: string }> {
  const positions = new Map<string, { pageIndex: number; slotIndex: number; slotKey: string }>();
  const orderedCards = setBinderSectionOrderedCards(cards);
  const slotsPerPage = binderSlotsPerPage(layout);
  let nextIndex = SET_BINDER_FIRST_PAGE_INDEX * slotsPerPage;

  // Compact 3×2 pages pack densely so tiny sets (e.g. Arcane) fill one page.
  if (layout === COMPACT_SET_BINDER_LAYOUT && orderedCards.length <= slotsPerPage) {
    for (const card of orderedCards) {
      positions.set(card.id, binderSlotPositionForIndex(nextIndex, layout));
      nextIndex += 1;
    }
    return positions;
  }

  const groupSizes = setBinderGroupSizes(cards);
  let previousSpacingGroup: string | null = null;

  for (const card of orderedCards) {
    const group = setBinderPageGroup(card);
    const spacingGroup = setBinderSpacingGroup(group);
    if (previousSpacingGroup !== null && spacingGroup !== previousSpacingGroup) {
      nextIndex = nextSetBinderGroupStartIndex(
        nextIndex,
        layout,
        previousSpacingGroup,
        spacingGroup,
        groupSizes.get(group) ?? 1,
      );
    }

    positions.set(card.id, binderSlotPositionForIndex(nextIndex, layout));
    nextIndex += 1;
    previousSpacingGroup = spacingGroup;
  }

  return positions;
}

export function buildDefaultSetPlacements({
  cards,
  entries,
  layout,
}: {
  cards: RiftboundCard[];
  entries: Record<string, CollectionEntry>;
  layout: BinderLayout;
}): BinderPlacementDraft[] {
  const sortedCards = sortBinderCardsByCollectorNumber(cards);
  const positionsByCardId = buildSetBinderSlotPositions(sortedCards, layout);

  return sortedCards
    .filter((card) => (entries[card.id]?.quantity ?? 0) > 0)
    .map((card) => {
      const position = positionsByCardId.get(card.id) ?? binderSlotPositionForIndex(0, layout);
      return {
        cardId: card.id,
        pageIndex: position.pageIndex,
        slotIndex: position.slotIndex,
        foil: (entries[card.id]?.quantityFoil ?? 0) > 0,
      };
    });
}

export function countPlacedCopiesByCard(
  placements: BinderPlacement[],
  excludeSlotKeys: string[] = [],
): Record<string, { total: number; foil: number }> {
  const excluded = new Set(excludeSlotKeys);
  const counts: Record<string, { total: number; foil: number }> = {};
  for (const placement of placements) {
    if (excluded.has(placement.slotKey)) continue;
    const current = counts[placement.cardId] ?? { total: 0, foil: 0 };
    current.total += 1;
    if (placement.foil) current.foil += 1;
    counts[placement.cardId] = current;
  }
  return counts;
}

/** Owned copies of one card that are not yet placed in the binder. */
export function remainingCopiesForCard(
  cardId: string,
  entries: Record<string, CollectionEntry>,
  placedCounts: Record<string, { total: number; foil: number }>,
): number {
  const owned = entries[cardId]?.quantity ?? 0;
  if (owned <= 0) return 0;
  return Math.max(0, owned - (placedCounts[cardId]?.total ?? 0));
}

/** Totals for the custom-binder sidebar (remaining copies vs slots filled). */
export function summarizeBinderOrganization(
  ownedCardIds: string[],
  entries: Record<string, CollectionEntry>,
  placements: BinderPlacement[],
): { remainingCopies: number; placedCopies: number } {
  const placedCounts = countPlacedCopiesByCard(placements);
  let remainingCopies = 0;
  for (const cardId of ownedCardIds) {
    remainingCopies += remainingCopiesForCard(cardId, entries, placedCounts);
  }
  return { remainingCopies, placedCopies: placements.length };
}

// ── 3D scene geometry helpers ─────────────────────────────────────────────

/**
 * Height of a single binder page in Three.js world units.
 * Width is layout-derived so that every pocket has exactly the 5:7 card aspect ratio.
 */
export const BINDER_PAGE_HEIGHT_WU = 1.71;

/**
 * Width of a single binder page in world units.
 * Portrait layouts: derived from the 5:7 card aspect ratio × (columns / rows).
 * Landscape layouts (cards rotated 90°): use the 7:5 ratio so each pocket's wider
 * dimension matches the card's long edge.
 *   Example 3×3 portrait: (3/3)×(5/7)×1.71 ≈ 1.22 WU
 *   Example 4×2 landscape: (2/4)×(7/5)×1.71 ≈ 1.20 WU
 */
export function binderPageWidthWU(layout: BinderLayout): number {
  const spec = BINDER_LAYOUTS[layout];
  const cardRatio = spec.landscape ? (7 / 5) : (5 / 7);
  return BINDER_PAGE_HEIGHT_WU * (spec.columns / spec.rows) * cardRatio;
}

// ── Shared geometry constants (must match BinderScene) ────────────────────
/**
 * Width of the spine / centre gutter between the two pages, in world units.
 * Kept small so the two folded pages meet near the middle with only a slim
 * binding strip between them (real open-binder look).
 */
export const BINDER_SPINE_W  = 0.06;
/** Cover margin (extra width/height beyond the page + spine). */
export const BINDER_COVER_MARGIN = 0.12;
/** Camera Z position in the 3D scene. */
export const BINDER_CAMERA_Z   = 4;
/** Camera vertical FOV (degrees). */
export const BINDER_CAMERA_FOV = 30;
/** Z position of pocket surfaces in world units. */
export const BINDER_POCKET_Z   = 0.04;
/**
 * Open-book fold: each page tilts up from the spine by this many degrees so the
 * two pages meet in a shallow "V" valley at the gutter (real zip-folio look).
 * Shared by the 3D scene (page mesh rotation) and the CSS pocket overlay
 * (per-side rotateY) so pockets stay glued to the page faces.
 */
export const BINDER_PAGE_FOLD_DEG = 11;
/**
 * Inset of each dark pocket page face vs the full PAGE_W / PAGE_H in BinderScene.
 * The coloured cover border this wide is visible around each page.
 * Must match the PAGE_INSET local constant in BinderScene.tsx.
 */
export const BINDER_PAGE_INSET = 0.035;
/**
 * Baked-in X rotation (radians) of the inner page group in BinderScene.
 * Negative = top of page tilts away from viewer ("lying on a table" look).
 * The CSS pocket overlay must apply the equivalent CSS rotateX to match:
 *   rotateX(deg) = −BINDER_PAGE_ROT_X × (180/π)
 */
export const BINDER_PAGE_ROT_X = -0.05;

/**
 * CSS aspect-ratio (width / height) for the binder container element.
 *
 * Derived from the cover body dimensions in the 3D scene:
 *   cover_width  = 2 × PAGE_W + SPINE_W + COVER_MARGIN
 *   cover_height = PAGE_H + COVER_MARGIN
 */
export function binderContainerAspect(layout: BinderLayout): number {
  const PAGE_W = binderPageWidthWU(layout);
  return (2 * PAGE_W + BINDER_SPINE_W + BINDER_COVER_MARGIN) /
         (BINDER_PAGE_HEIGHT_WU + BINDER_COVER_MARGIN);
}

// ── Pocket gap / padding constants (must match BinderScene) ───────────────
const POCKET_GAP = 0.016;
/**
 * Horizontal padding from PAGE_W edge to the pocket grid, in world units.
 * Derived from BINDER_PAGE_INSET so the grid is always inside the dark page faces,
 * with an additional 0.022 WU of breathing room regardless of layout.
 */
const POCKET_PAD_H = BINDER_PAGE_INSET + 0.022; // 0.057 WU

/**
 * Exact CSS % positions (left / top / width / height) for every pocket in the
 * current spread, computed using the same perspective math as the 3D scene.
 *
 * Approach:
 *   • SPREAD_OFFSET = SPINE_W/2 + PAGE_W/2  (page centre = half-spine + half-page)
 *   • Camera at Z = BINDER_CAMERA_Z, fov = BINDER_CAMERA_FOV (vertical)
 *   • Pockets sit at Z = BINDER_POCKET_Z, so depth d = CAMERA_Z – POCKET_Z
 *   • tan(fov/2) × d = visible half-height; × aspect = visible half-width
 *   • NDC → CSS %: left = (0.5 + NDC_x/2)×100 %, top = (0.5 – NDC_y/2)×100 %
 */
export interface PocketCSSPosition {
  left:      string; // CSS %
  top:       string; // CSS %
  width:     string; // CSS %
  height:    string; // CSS %
  slotKey:   string;
  pageIndex: number;
  slotIndex: number;
}

export function binderPocketCSSLayout(
  layout:        BinderLayout,
  leftPageIndex: number,
): PocketCSSPosition[] {
  const spec  = BINDER_LAYOUTS[layout];
  const PAGE_W = binderPageWidthWU(layout);
  const PAGE_H = BINDER_PAGE_HEIGHT_WU;

  // Where each page backing is centred (half-spine + half-page)
  const SPREAD_OFFSET = BINDER_SPINE_W / 2 + PAGE_W / 2;

  // Perspective half-extents at pocket depth
  const d         = BINDER_CAMERA_Z - BINDER_POCKET_Z;
  const tanHalf   = Math.tan((BINDER_CAMERA_FOV / 2) * (Math.PI / 180));
  const aspect    = binderContainerAspect(layout);
  const visHalfW  = d * tanHalf * aspect; // visible half-width  in WU
  const visHalfH  = d * tanHalf;          // visible half-height in WU

  // Pocket dimensions — PAD_H is fixed in WU so grid stays inside dark page faces.
  // Portrait: pocketH = pocketW × 7/5 (tall card).
  // Landscape (cards rotated 90°): pocketH = pocketW × 5/7 (wide card in a short pocket).
  const landscape = spec.landscape ?? false;
  const pocketW = (PAGE_W - 2 * POCKET_PAD_H - (spec.columns - 1) * POCKET_GAP) / spec.columns;
  const pocketH = landscape ? pocketW * (5 / 7) : pocketW * (7 / 5);
  const usedH   = spec.rows * pocketH + (spec.rows - 1) * POCKET_GAP;
  const PAD_V   = (PAGE_H - usedH) / 2;

  const cssW = `${(pocketW / (2 * visHalfW)) * 100}%`;
  const cssH = `${(pocketH / (2 * visHalfH)) * 100}%`;

  const result: PocketCSSPosition[] = [];

  for (let side = -1; side <= 1; side += 2) {
    const pageIndex  = leftPageIndex + (side < 0 ? 0 : 1);
    const pageOffX   = side * SPREAD_OFFSET;
    const startX     = pageOffX - PAGE_W / 2 + POCKET_PAD_H + pocketW / 2;
    const startY     = PAGE_H  / 2 - PAD_V   - pocketH / 2;

    for (let i = 0; i < spec.rows * spec.columns; i++) {
      const r  = Math.floor(i / spec.columns);
      const c  = i % spec.columns;
      const cx = startX + c * (pocketW + POCKET_GAP);
      const cy = startY - r * (pocketH + POCKET_GAP);

      // Top-left corner in NDC → CSS %
      const left = (0.5 + (cx - pocketW / 2) / visHalfW  * 0.5) * 100;
      const top  = (0.5 - (cy + pocketH / 2) / visHalfH  * 0.5) * 100;

      result.push({
        left:      `${left}%`,
        top:       `${top}%`,
        width:     cssW,
        height:    cssH,
        slotKey:   `p${pageIndex}-s${i}`,
        pageIndex,
        slotIndex: i,
      });
    }
  }

  return result;
}

export function validateBinderPlacement({
  placement,
  placements,
  entries,
  excludeSlotKeys = [],
  pageLimit,
}: {
  placement: Pick<BinderPlacementDraft, 'cardId' | 'foil' | 'pageIndex'>;
  placements: BinderPlacement[];
  entries: Record<string, CollectionEntry>;
  excludeSlotKeys?: string[];
  /** When set, reject placements that exceed the binder's fixed page count. */
  pageLimit?: number;
}): { valid: true } | { valid: false; reason: string } {
  if (pageLimit !== undefined && placement.pageIndex >= pageLimit) {
    return { valid: false, reason: 'This slot is outside the binder\'s page range.' };
  }

  const entry = entries[placement.cardId];
  if (!entry || entry.quantity <= 0) {
    return { valid: false, reason: 'You do not own this card yet.' };
  }

  const counts = countPlacedCopiesByCard(placements, excludeSlotKeys);
  const existing = counts[placement.cardId] ?? { total: 0, foil: 0 };
  if (existing.total + 1 > entry.quantity) {
    return { valid: false, reason: 'All owned copies of this card are already in this binder.' };
  }
  if (placement.foil && existing.foil + 1 > entry.quantityFoil) {
    return { valid: false, reason: 'All owned foil copies of this card are already in this binder.' };
  }
  return { valid: true };
}
